import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Sparkles, Brain, FileText, Lightbulb, CheckCircle2, AlertTriangle,
  ChevronDown, ChevronRight, Loader2, RefreshCw, Shield,
  Users, TrendingUp, Target, MessageSquare, ListChecks, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section icons mapping
const SECTION_ICONS = {
  segue: Users,
  scorecard: TrendingUp,
  rocks: Target,
  headlines: MessageSquare,
  todos: ListChecks,
  ids: Zap,
  conclude: CheckCircle2
};

// Confidence badge styling
const getConfidenceBadge = (score) => {
  if (!score && score !== 0) return null;
  const percentage = Math.round(score * 100);
  if (percentage >= 80) {
    return <Badge className="bg-green-600 text-xs">High {percentage}%</Badge>;
  } else if (percentage >= 60) {
    return <Badge className="bg-yellow-600 text-xs">Medium {percentage}%</Badge>;
  }
  return <Badge variant="outline" className="text-xs">Low {percentage}%</Badge>;
};

/**
 * AI Copilot Panel - In-meeting AI assistance
 * Displays AI-generated notes, decisions, and actions by section
 */
const AICopilotPanel = ({
  meetingId,
  currentSection = 'segue',
  copilotEnabled = false,
  isFacilitator = false,
  onEnableCopilot,
  sectionData = {},
  participants = [],
  className = ""
}) => {
  const [activeTab, setActiveTab] = useState(currentSection);
  const [typedNotes, setTypedNotes] = useState({});
  const [aiNotes, setAiNotes] = useState({});
  const [aiPrompts, setAiPrompts] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ summary: true, decisions: true, actions: true });

  // Update active tab when section changes
  useEffect(() => {
    setActiveTab(currentSection);
    if (copilotEnabled && !aiPrompts[currentSection]) {
      fetchFacilitatorPrompts(currentSection);
    }
  }, [currentSection, copilotEnabled]);

  const fetchFacilitatorPrompts = async (section) => {
    try {
      const response = await axios.get(
        `${API}/eos/meetings/${meetingId}/copilot/facilitator-prompts?section=${section}`
      );
      setAiPrompts(prev => ({ ...prev, [section]: response.data.prompts }));
    } catch (error) {
      console.error('Error fetching prompts:', error);
    }
  };

  const handleGenerateNotes = async (section) => {
    if (!copilotEnabled) {
      toast.error('Enable AI Copilot first');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/eos/meetings/${meetingId}/copilot/section-notes`,
        {
          section,
          section_data: sectionData[section] || {},
          typed_notes: typedNotes[section] || ''
        }
      );

      setAiNotes(prev => ({ ...prev, [section]: response.data }));
      toast.success('AI notes generated');
    } catch (error) {
      console.error('Error generating notes:', error);
      toast.error('Failed to generate AI notes');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const currentNotes = aiNotes[activeTab];
  const currentPrompts = aiPrompts[activeTab];

  // Not enabled state
  if (!copilotEnabled) {
    return (
      <Card className={className}>
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            AI Copilot
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-6">
          <Sparkles className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-3">
            Enable AI Copilot for intelligent meeting assistance
          </p>
          {isFacilitator && onEnableCopilot && (
            <Button onClick={onEnableCopilot} className="bg-purple-600 hover:bg-purple-700">
              <Shield className="h-4 w-4 mr-2" />
              Enable with Consent
            </Button>
          )}
          {!isFacilitator && (
            <p className="text-xs text-muted-foreground">
              Only the facilitator can enable AI Copilot
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Sections for tabs
  const sections = [
    { key: 'segue', label: 'Segue' },
    { key: 'scorecard', label: 'Scorecard' },
    { key: 'rocks', label: 'Rocks' },
    { key: 'ids', label: 'IDS' },
    { key: 'conclude', label: 'Wrap-up' }
  ];

  return (
    <Card className={`border-purple-300 dark:border-purple-800 ${className}`}>
      <CardHeader className="py-2 pb-0">
        <CardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600" />
            AI Copilot
            <Badge className="bg-purple-600 text-xs py-0">ON</Badge>
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-2">
        {/* Section Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full h-auto flex flex-wrap gap-1 bg-muted/50 p-1">
            {sections.map(section => {
              const Icon = SECTION_ICONS[section.key];
              const hasNotes = !!aiNotes[section.key];
              return (
                <TabsTrigger
                  key={section.key}
                  value={section.key}
                  className="flex-1 text-xs py-1 px-2 min-w-0 data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  <Icon className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="truncate">{section.label}</span>
                  {hasNotes && <CheckCircle2 className="h-2 w-2 ml-1 text-green-400" />}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {sections.map(section => (
            <TabsContent key={section.key} value={section.key} className="mt-2 space-y-3">
              {/* Facilitator Prompts */}
              {currentPrompts?.opening?.length > 0 && activeTab === section.key && (
                <div className="p-2 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="text-xs font-medium mb-1 flex items-center gap-1 text-yellow-700 dark:text-yellow-300">
                    <Lightbulb className="h-3 w-3" />
                    Facilitator Tips
                  </div>
                  <div className="space-y-1">
                    {currentPrompts.opening.slice(0, 2).map((prompt, idx) => (
                      <p key={idx} className="text-xs text-yellow-800 dark:text-yellow-200">
                        • {prompt}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes Input */}
              <div>
                <Label className="text-xs">Meeting Notes</Label>
                <Textarea
                  placeholder={`Add notes for ${section.label}...`}
                  value={typedNotes[section.key] || ''}
                  onChange={(e) => setTypedNotes(prev => ({ ...prev, [section.key]: e.target.value }))}
                  rows={2}
                  className="text-xs mt-1"
                />
              </div>

              {/* Generate Button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => handleGenerateNotes(section.key)}
                disabled={loading}
              >
                {loading && activeTab === section.key ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Brain className="h-4 w-4 mr-1" />
                )}
                Generate AI Notes
              </Button>

              {/* AI Generated Notes */}
              {aiNotes[section.key] && (
                <ScrollArea className="h-[350px]">
                  <div className="space-y-2 pr-2">
                    {/* Summary */}
                    <Collapsible open={expandedSections.summary} onOpenChange={() => toggleSection('summary')}>
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-950/30 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-950/50 transition-colors">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-purple-600" />
                            <span className="text-xs font-semibold">Summary</span>
                          </div>
                          {expandedSections.summary ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-2 space-y-1">
                          {aiNotes[section.key].summary?.map((item, idx) => (
                            <div key={idx} className="text-xs flex items-start gap-2">
                              <span className="text-purple-600">•</span>
                              <span>{item}</span>
                            </div>
                          ))}
                          {(!aiNotes[section.key].summary || aiNotes[section.key].summary.length === 0) && (
                            <p className="text-xs text-muted-foreground italic">No summary points generated</p>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Decisions */}
                    <Collapsible open={expandedSections.decisions} onOpenChange={() => toggleSection('decisions')}>
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950/30 rounded-lg hover:bg-green-100 dark:hover:bg-green-950/50 transition-colors">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <span className="text-xs font-semibold">Decisions</span>
                            {aiNotes[section.key].decisions?.length > 0 && (
                              <Badge variant="secondary" className="text-[10px] py-0">
                                {aiNotes[section.key].decisions.length}
                              </Badge>
                            )}
                          </div>
                          {expandedSections.decisions ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-2 space-y-2">
                          {aiNotes[section.key].decisions?.map((decision, idx) => (
                            <div key={idx} className="p-2 bg-muted rounded text-xs">
                              <div className="font-medium">{decision.decision || decision}</div>
                              {decision.context && (
                                <div className="text-muted-foreground mt-1">{decision.context}</div>
                              )}
                              {decision.owner && (
                                <div className="text-green-600 mt-1">Owner: {decision.owner}</div>
                              )}
                            </div>
                          ))}
                          {(!aiNotes[section.key].decisions || aiNotes[section.key].decisions.length === 0) && (
                            <p className="text-xs text-muted-foreground italic">No decisions captured</p>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Suggested Actions */}
                    <Collapsible open={expandedSections.actions} onOpenChange={() => toggleSection('actions')}>
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-950/50 transition-colors">
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-blue-600" />
                            <span className="text-xs font-semibold">Suggested Actions</span>
                            {aiNotes[section.key].suggested_actions?.length > 0 && (
                              <Badge variant="secondary" className="text-[10px] py-0">
                                {aiNotes[section.key].suggested_actions.length}
                              </Badge>
                            )}
                          </div>
                          {expandedSections.actions ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-2 space-y-2">
                          {aiNotes[section.key].suggested_actions?.map((action, idx) => (
                            <div key={idx} className="p-2 border rounded-lg text-xs">
                              <div className="flex items-start justify-between gap-2">
                                <span className="font-medium flex-1">
                                  {typeof action === 'string' ? action : action.action}
                                </span>
                                {action.priority && (
                                  <Badge 
                                    variant={action.priority === 'high' ? 'destructive' : 'outline'}
                                    className="text-[10px] py-0"
                                  >
                                    {action.priority}
                                  </Badge>
                                )}
                              </div>
                              {action.owner_suggestion && (
                                <div className="text-muted-foreground mt-1">
                                  Suggested owner: {action.owner_suggestion}
                                </div>
                              )}
                              {action.due_suggestion && (
                                <div className="text-muted-foreground">
                                  Due: {action.due_suggestion}
                                </div>
                              )}
                            </div>
                          ))}
                          {(!aiNotes[section.key].suggested_actions || aiNotes[section.key].suggested_actions.length === 0) && (
                            <p className="text-xs text-muted-foreground italic">No actions suggested</p>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Risks/Blockers */}
                    {aiNotes[section.key].risks_blockers?.length > 0 && (
                      <div className="p-2 bg-orange-50 dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-800">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-4 w-4 text-orange-600" />
                          <span className="text-xs font-semibold text-orange-700 dark:text-orange-300">Risks & Blockers</span>
                        </div>
                        {aiNotes[section.key].risks_blockers.map((risk, idx) => (
                          <div key={idx} className="text-xs text-orange-800 dark:text-orange-200 mb-1">
                            • {risk.issue || risk}
                            {risk.severity && <Badge variant="outline" className="ml-1 text-[10px] py-0">{risk.severity}</Badge>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Confidence Score */}
                    {aiNotes[section.key].confidence_score !== undefined && (
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-xs text-muted-foreground">AI Confidence</span>
                        {getConfidenceBadge(aiNotes[section.key].confidence_score)}
                      </div>
                    )}

                    {aiNotes[section.key].is_mock && (
                      <div className="text-[10px] text-center text-muted-foreground pt-2">
                        Demo mode - configure API key for real AI analysis
                      </div>
                    )}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AICopilotPanel;
